import {
	TextField,
	Grid,
	Box,
	Typography,
	Button,
	Toolbar,
	Badge,
	ClickAwayListener,
	Paper,
	Popper,
	Stack,
	Tooltip,
	ButtonGroup,
	MenuItem,
} from '@mui/material'
import {
	ColumnsPanelTrigger,
	ExportCsv,
	ExportPrint,
	FilterPanelTrigger,
	GridColDef,
	GridRowSelectionModel,
	QuickFilter,
	QuickFilterClear,
	QuickFilterControl,
	QuickFilterTrigger,
	ToolbarButton,
	useGridApiContext,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { alertModal, confirmAlert } from '../../../util/util'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { AskSearchType, AskType } from 'constants/appType'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiAskList, apiAskSave } from 'api/api'
import { SpRequestType } from 'constants/types'
import dayjs from 'dayjs'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import clsx from 'clsx'
import AddIcon from '@mui/icons-material/Add'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import React from 'react'

// React Query 키 팩토리
const askKeys = {
	all: ['ask'] as const,
	lists: () => [...askKeys.all, 'list'] as const,
	list: (params: AskSearchType) => [...askKeys.lists(), params] as const,
}

const Order = () => {
	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const disabledAdd = true
	const [disabledDel, setDisabledDel] = useState(true)
	const [disabledSave, setDisabledSave] = useState(true)
	const disabledSearch = false
	const disabledReset = false
	const disabledExcelDownload = false
	const hideAdd = true
	const hideDel = true
	const hideSave = true
	const hideSearch = false
	const hideReset = false
	const hideExcelDownload = true
	const [loading, setLoading] = useState(false)

	const queryClient = useQueryClient()

	const gridColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'id',
				headerName: 'id',
				width: 90,
			},
			{
				field: 'crud',
				headerName: 'crud',
				width: 90,
			},
			{
				field: 'userId',
				headerName: '주문번호',
				width: 150,
			},
			{
				field: 'requestMessage',
				headerName: '주문일',
				minWidth: 300,
			},
			{
				field: 'answerMessage',
				headerName: '주문자',
				minWidth: 300,
			},
			{
				field: 'regId',
				headerName: '상품명',
				width: 150,
			},
			{
				field: 'aa',
				headerName: '수량',
				width: 100,
			},
			{
				field: 'bb',
				headerName: '총금액',
				width: 150,
			},
			{
				field: 'cc',
				headerName: '배송상태',
				width: 150,
			},
			{
				field: 'dd',
				headerName: '운송장',
				width: 150,
			},
			{
				field: 'action',
				headerName: '액션',
				width: 150,
				renderCell: (params) => {
					return (
						<>
							<Button variant="contained">상세</Button>
							<Button variant="contained">배송조회</Button>
						</>
					)
				},
			},
		],
		[],
	)

	const [search, setSearch] = useState<AskSearchType>({
		id: 0,
		userId: 0,
		regId: '',
		modId: '',
		msg: '',
		startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		endDate: dayjs().format('YYYY-MM-DD'),
		type: 'all',
		keyword: '',
	})

	// React Query를 사용한 게시판 목록 조회
	const {
		data: askData = [],
		isLoading,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: askKeys.list(search),
		queryFn: async () => {
			setLoading(true)
			console.log('apiBoardList 호출 시작 - 검색 파라미터:', search)
			const result = await apiAskList(search).finally(() => setLoading(false))
			console.log('apiBoardList 호출 성공 - 결과:', result)
			return result
		},
		staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
		gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지

		// 자동 리페칭 옵션들
		refetchOnWindowFocus: true, // 윈도우 포커스 시 자동 리페칭
		refetchOnReconnect: true, // 네트워크 재연결 시 자동 리페칭
		refetchOnMount: true, // 컴포넌트 마운트 시 자동 리페칭

		// 주기적 리페칭
		refetchInterval: 10 * 60 * 1000, // 30분마다 자동 리페칭
		refetchIntervalInBackground: false, // 백그라운드에서는 리페칭 안함
	})

	// 추가
	const actionAdd = () => {}

	// 삭제
	const actionDel = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('삭제할 문의를 선택해주세요.', 'warning', () => {})
			return
		}

		const selectedRows: AskType[] = selectedIds.map((id) => {
			return apiRef.current!.getRow(id) as AskType
		})

		// 저장 로직 구현 (api 호출 등)
		console.log('삭제할 문의:', selectedRows)

		// selectedRows에서 crud값을 D로 변경
		const rowsToUpdate = selectedRows.map((row) => ({
			...row,
			crud: 'D',
		}))

		if (rowsToUpdate.length === 0) {
			alertModal('삭제할 변경된 문의가 없습니다.', 'info', () => {})
			return
		}

		if (rowsToUpdate.length > 0) {
			confirmAlert(
				'선택한 문의를 삭제하시겠습니까?',
				() => {
					const request: SpRequestType = {
						cmd: 'DELETE',
						jsonText: JSON.stringify(rowsToUpdate),
					}

					setDisabledSave(true)
					apiAskSave(request)
						.then((res) => {
							console.log('문의 저장 결과:', res)
							alertModal('문의가 성공적으로 저장되었습니다.', 'success', () => {
								// 저장 후 목록 다시 조회
								queryClient.invalidateQueries({
									queryKey: askKeys.lists(),
								})
							})
						})
						.finally(() => {
							setDisabledSave(false)
						})
				},
				() => {},
			)
		}
	}

	// 저장
	const actionSave = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('저장할 문의를 선택해주세요.', 'warning', () => {})
			return
		}

		const selectedRows: AskType[] = selectedIds.map((id) => {
			return apiRef.current!.getRow(id) as AskType
		})

		// 저장 로직 구현 (api 호출 등)
		console.log('저장할 문의:', selectedRows)

		// selectedRows에서 crud값이 U 인것만 선택
		const rowsToUpdate = selectedRows.filter((row) => row.crud === 'U')

		if (rowsToUpdate.length === 0) {
			alertModal('저장할 변경된 문의가 없습니다.', 'info', () => {})
			return
		}

		if (rowsToUpdate.length > 0) {
			confirmAlert(
				'선택한 문의를 저장하시겠습니까?',
				() => {
					const request: SpRequestType = {
						cmd: 'UPDATE',
						jsonText: JSON.stringify(rowsToUpdate),
					}

					setDisabledSave(true)
					apiAskSave(request)
						.then((res) => {
							console.log('문의 저장 결과:', res)
							alertModal('문의가 성공적으로 저장되었습니다.', 'success', () => {
								// 저장 후 목록 다시 조회
								queryClient.invalidateQueries({
									queryKey: askKeys.lists(),
								})
							})
						})
						.finally(() => {
							setDisabledSave(false)
						})
				},
				() => {},
			)
		}
	}

	// 검색 (React Query refetch 사용)
	const actionSearch = useCallback(() => {
		refetch()
	}, [refetch])

	// 행 업데이트 처리
	const processRowUpdate = (newRow: any, oldRow: any) => {
		if (newRow.answerMessage !== oldRow.answerMessage) {
			newRow.crud = 'U' // 수정 상태로 변경
		}
		return newRow
	}

	// 행 업데이트 에러 처리
	const handleProcessRowUpdateError = (error: Error) => {
		console.error('Row update error:', error)
		alertModal('데이터 업데이트 중 오류가 발생했습니다.', 'error', () => {})
	}

	// 초기화
	const actionReset = useCallback(() => {
		const initialSearch: AskSearchType = {
			id: 0,
			userId: 0,
			regId: '',
			modId: '',
			msg: '',
			startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
			endDate: dayjs().format('YYYY-MM-DD'),
			type: 'all',
			keyword: '',
		}
		setSearch(initialSearch)
		setRowSelectionModel({ type: 'include', ids: new Set() })

		// 그리드 상태 초기화
		if (apiRef.current) {
			apiRef.current.setPage(0)
			apiRef.current.setFilterModel({
				items: [],
			})
			apiRef.current.setSortModel([])
			apiRef.current.setColumnVisibilityModel({})
		}

		// 캐시 무효화 및 새로운 데이터 가져오기
		queryClient.invalidateQueries({
			queryKey: askKeys.lists(),
		})
	}, [queryClient, apiRef])

	// 키보드 엔터키 처리
	const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter' || e.key === 'Enter') {
			e.preventDefault() // 폼 제출 방지
			actionSearch()
		}
	}

	// 폼 제출 방지
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	useEffect(() => {
		actionSearch()
	}, [])

	// 선택된 행에 따른 삭제 버튼 활성화/비활성화
	useEffect(() => {
		setDisabledDel(rowSelectionModel.ids.size === 0)
		setDisabledSave(rowSelectionModel.ids.size === 0)
	}, [rowSelectionModel.ids.size])

	// 검색 파라미터 변경 시 자동 검색 (debounce 적용)
	// search 상태가 변경되면 queryKey가 변경되어 자동으로 리페칭됨
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			// search 상태가 변경되면 React Query가 자동으로 새로운 쿼리를 실행
		}, 500) // 500ms debounce

		return () => clearTimeout(timeoutId)
	}, [search.id, search.userId, search.regId, search.modId])

	return (
		<PageContainer>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer
					onKeyDown={onKeyDown}
					onSubmit={onSubmit}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid size={4}>
							<LocalizationProvider
								dateAdapter={AdapterMoment}
								adapterLocale="ko"
							>
								<Box
									sx={{
										display: 'flex',
										gap: 2,
										alignItems: 'center',
										width: '100%',
									}}
								>
									<DatePicker
										label="시작일"
										value={moment(search.startDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, startDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
									<Typography>~</Typography>
									<DatePicker
										label="종료일"
										value={moment(search.endDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, endDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
										sx={{ width: '100%' }}
									/>
								</Box>
							</LocalizationProvider>
						</Grid>
						<Grid size={4}>
							<TextField
								label="주문자명/전화번호"
								variant="outlined"
								fullWidth
								size="small"
							/>
						</Grid>
						<Grid size={4}>
							<TextField
								label="상품"
								variant="outlined"
								fullWidth
								size="small"
								select
							>
								<MenuItem value="all">전체</MenuItem>
								<MenuItem value="title">갤럭시워치</MenuItem>
								<MenuItem value="content">애플워치</MenuItem>
								<MenuItem value="content">샤오미밴드</MenuItem>
							</TextField>
						</Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				<Box sx={{ height: 'calc(100% - 35px)' }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
						<ButtonGroup>
							<Button
								variant="contained"
								color="primary"
							>
								전체 (100)
							</Button>
							<Button
								variant="outlined"
								color="secondary"
							>
								주문접수 (100)
							</Button>
							<Button
								variant="outlined"
								color="secondary"
							>
								배송중 (100)
							</Button>
							<Button
								variant="outlined"
								color="secondary"
							>
								배송완료 (100)
							</Button>
						</ButtonGroup>
						<Button
							variant="contained"
							color="secondary"
						>
							주문등록
						</Button>
					</Box>
					<StyledDataGrid
						apiRef={apiRef}
						columns={gridColumns}
						rows={askData}
						showToolbar
						loading={loading}
						localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
						initialState={{
							columns: {
								columnVisibilityModel: {
									id: false,
									userId: false,
									crud: false,
								},
							},
						}}
					/>
				</Box>
			</GridContainer>
		</PageContainer>
	)
}

export default Order
